import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Plugin, TextSelection, PluginKey } from 'prosemirror-state';
import { findParentNodeOfType } from 'prosemirror-utils';
import { DecorationSet, Decoration } from 'prosemirror-view';
import { keydownHandler } from 'prosemirror-keymap';
import { layoutColumn, layoutSection } from './nodes';
import { alignment, indentation } from './marks';
import { getIconItem } from '../../utils/menu';
import { filter } from '../../utils/slice';
import { fixColumnSizes, getSelectedLayout, fixColumnStructure, insertLayoutColumns, setPluginKey } from './actions';
import { FLOATING_TOOLBAR_ITEMS } from './constants';
import './Layout.scss';
const DEFAULT_LAYOUT = 'two_equal';
const pluginKey = new PluginKey('layout');
setPluginKey(pluginKey);
const isWholeSelectionInsideLayoutColumn = state => {
  // Since findParentNodeOfType doesn't check if selection.to shares the parent, we do this check ourselves
  const fromParent = findParentNodeOfType(state.schema.nodes.layoutColumn)(state.selection);
  if (fromParent) {
    const isToPosInsideSameLayoutColumn = state.selection.from < fromParent.pos + fromParent.node.nodeSize;
    return isToPosInsideSameLayoutColumn;
  }
  return false;
};
const moveCursorToNextColumn = (state, dispatch) => {
  const {
    selection
  } = state;
  const {
    schema: {
      nodes: {
        layoutColumn: columnNode,
        layoutSection: sectionNode
      }
    }
  } = state;
  const section = findParentNodeOfType(sectionNode)(selection);
  const column = findParentNodeOfType(columnNode)(selection);
  if (column && column.node !== section.node.lastChild) {
    const $nextColumn = state.doc.resolve(column.pos + column.node.nodeSize);
    const shiftedSelection = TextSelection.findFrom($nextColumn, 1);
    if (dispatch) {
      dispatch(state.tr.setSelection(shiftedSelection));
    }
  }
  return true;
};
const moveCursorToNextNode = (state, dispatch) => {
  const {
    selection
  } = state;
  const {
    schema: {
      nodes: {
        layoutColumn: columnNode,
        layoutSection: sectionNode
      }
    }
  } = state;
  const section = findParentNodeOfType(sectionNode)(selection);
  const column = findParentNodeOfType(columnNode)(selection);
  if (column && column.node === section.node.lastChild) {
    const nextPos = section.pos + section.node.nodeSize;
    const $nextNode = state.doc.resolve(nextPos);
    if (!$nextNode.nodeAfter) {
      dispatch(state.tr.replaceWith(nextPos, nextPos, state.schema.nodes.paragraph.create({})));
    }
  }
};

// TODO: Look at memoize-one-ing this fn
const getNodeDecoration = (pos, node) => [Decoration.node(pos, pos + node.nodeSize, {
  class: 'selected'
})];
const getInitialPluginState = (pluginConfig, state) => {
  const maybeLayoutSection = findParentNodeOfType(state.schema.nodes.layoutSection)(state.selection);
  const allowBreakout = typeof pluginConfig === 'object' ? !!pluginConfig.allowBreakout : false;
  const addSidebarLayouts = typeof pluginConfig === 'object' ? !!pluginConfig.UNSAFE_addSidebarLayouts : false;
  const pos = maybeLayoutSection ? maybeLayoutSection.pos : null;
  const selectedLayout = getSelectedLayout(maybeLayoutSection && maybeLayoutSection.node, DEFAULT_LAYOUT);
  return {
    pos,
    allowBreakout,
    addSidebarLayouts,
    selectedLayout,
    activeToolbarItem: selectedLayout
  };
};
const configureLayoutPlugin = pluginConfig => new Plugin({
  key: pluginKey,
  state: {
    init: (_, state) => getInitialPluginState(pluginConfig, state),
    apply: (tr, pluginState, _oldState, newState) => {
      if (tr.docChanged || tr.selectionSet) {
        const maybeLayoutSection = findParentNodeOfType(newState.schema.nodes.layoutSection)(newState.selection);
        const selectedLayout = getSelectedLayout(maybeLayoutSection && maybeLayoutSection.node, pluginState.selectedLayout);
        const newPluginState = _objectSpread(_objectSpread({}, pluginState), {}, {
          pos: maybeLayoutSection ? maybeLayoutSection.pos : null,
          selectedLayout,
          activeToolbarItem: selectedLayout
        });
        return newPluginState;
      }
      return pluginState;
    }
  },
  props: {
    decorations(state) {
      const layoutState = pluginKey.getState(state);
      if (layoutState.pos !== null) {
        return DecorationSet.create(state.doc, getNodeDecoration(layoutState.pos, state.doc.nodeAt(layoutState.pos)));
      }
      return undefined;
    },
    handleKeyDown: keydownHandler({
      Tab: filter(isWholeSelectionInsideLayoutColumn, moveCursorToNextColumn),
      ArrowDown: moveCursorToNextNode,
      ArrowRight: moveCursorToNextNode
    })
  },
  appendTransaction: (transactions, _oldState, newState) => {
    const changes = [];
    transactions.forEach(prevTr => {
      // remap change segments across the transaction set
      changes.map(change => ({
        from: prevTr.mapping.map(change.from),
        to: prevTr.mapping.map(change.to),
        slice: change.slice
      }));

      // don't consider transactions that don't mutate
      if (!prevTr.docChanged) {
        return;
      }
      const change = fixColumnSizes(prevTr, newState);
      if (change) {
        changes.push(change);
      }
    });
    if (changes.length) {
      let {
        tr
      } = newState;
      const {
        selection
      } = newState;
      changes.forEach(change => {
        tr.replaceRange(change.from, change.to, change.slice);
      });

      // selecting and deleting across columns in 3 col layouts can remove
      // a layoutColumn so we fix the structure here
      tr = fixColumnStructure(newState) || tr;
      if (tr.docChanged) {
        if (selection.$from.doc === tr.doc) {
          tr.setSelection(selection);
        }
        tr.setMeta('addToHistory', false);
        return tr;
      }
    }
    return null;
  }
});
export default {
  name: 'layout',
  configure: options => ({
    nodes: {
      layoutColumn: layoutColumn(options),
      layoutSection: layoutSection(options),
      doc: {
        content: '(block | layoutSection | layoutColumn)+'
      }
    },
    marks: {
      alignment,
      indentation
    },
    plugin: configureLayoutPlugin(options),
    floatingToolbars: [{
      pluginKey,
      info: {
        items: FLOATING_TOOLBAR_ITEMS
      }
    }],
    menuItems: [getIconItem('insert-layout', 'tap-icon-editor-two-equal', 'Layouts', insertLayoutColumns)]
  })
};